/** POSTs data-issue reports; the key goes raw over HTTPS in `X-Api-Key` and the backend re-validates it. */

import { issueReporterUrl } from '#lib/config.js';

// Stops the form hanging on a dead connection; the abort surfaces as the modal's "couldn't reach" error.
const REQUEST_TIMEOUT_MS = 15_000;

export class IssueReporterError extends Error {
	constructor(readonly status: number) {
		super(`Issue Reporter ${status}`);
		this.name = 'IssueReporterError';
	}
}

/**
 * Ephemeral submission context — captured at report time because it can't be
 * reconstructed later. The backend stores it inside `location_info` and derives
 * indexed columns (e.g. `data_version`) from it. All fields best-effort.
 */
export interface ReportContext {
	/** The dataset's location label (name + state), e.g. "Pawnee Nation, Oklahoma". */
	area?: string;
	versions: { platform?: string; app?: string; data?: string; methodology?: string };
	view: {
		zoom?: number;
		center?: [number, number];
		bbox?: [number, number, number, number];
		basemap?: string;
		url?: string;
	};
	client: { userAgent?: string; viewport?: [number, number]; language?: string; timeZone?: string };
}

export interface ReportLocationInfo {
	app: string;
	systemId: string | null;
	layerId: string | null;
	featureId: string | number | null;
	position: string;
	properties: Record<string, unknown>;
	context?: ReportContext;
}

interface ReportInput {
	lat: number;
	lon: number;
	source: string;
	freetext: string;
	location_info: ReportLocationInfo;
	/** GeoJSON geometry of the reported feature; the backend stores it as a snapshot. */
	geometry?: GeoJSON.Geometry;
}

export async function submitReport(input: ReportInput, ctx: { apiKey: string }): Promise<void> {
	const response = await fetch(`${issueReporterUrl}/api/reports`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', 'X-Api-Key': ctx.apiKey },
		body: JSON.stringify(input),
		signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS)
	});
	if (!response.ok) throw new IssueReporterError(response.status);
}
